import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type {
  BlogAuthorDto,
  BlogCategoryDto,
  BlogCategoryListingPageData,
  BlogCountryListingPageData,
  BlogListItem,
  BlogListingPageData,
  BlogPageData,
  BlogPostDto,
  CreateBlogPostPayload,
  PublishValidationResult,
} from '../models/blog.models';

@Injectable({ providedIn: 'root' })
export class BlogsService {
  private readonly api = inject(ApiService);

  // ——— Public ———

  listPublic(page = 1, limit = 12, q?: string) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (q) params.set('q', q);
    return this.api
      .get<ApiEnvelope<BlogListingPageData>>(`public/blogs?${params}`)
      .pipe(map((r) => r.data));
  }

  getFeaturedPublic(limit = 3) {
    return this.api
      .get<ApiEnvelope<BlogListItem[]>>(`public/blogs/featured?limit=${limit}`)
      .pipe(map((r) => r.data));
  }

  getPublicBySlug(slug: string) {
    return this.api.get<ApiEnvelope<BlogPageData>>(`public/blogs/${slug}`).pipe(map((r) => r.data));
  }

  getPublicPreview(token: string) {
    return this.api
      .get<ApiEnvelope<BlogPageData>>(`public/blogs/preview/${token}`)
      .pipe(map((r) => r.data));
  }

  getPublicCategoryListing(slug: string, page = 1, limit = 12) {
    return this.api
      .get<ApiEnvelope<BlogCategoryListingPageData>>(
        `public/blogs/category/${slug}?page=${page}&limit=${limit}`,
      )
      .pipe(map((r) => r.data));
  }

  getPublicCountryListing(slug: string, page = 1, limit = 12) {
    return this.api
      .get<ApiEnvelope<BlogCountryListingPageData>>(
        `public/blogs/country/${slug}?page=${page}&limit=${limit}`,
      )
      .pipe(map((r) => r.data));
  }

  subscribeNewsletter(payload: {
    email: string;
    fullName?: string;
    source: string;
    sourcePage?: string;
    website?: string;
  }) {
    return this.api.post<ApiEnvelope<{ subscribed: boolean; email: string }>>(
      'public/newsletter/subscribe',
      payload,
    );
  }

  // ——— Platform ———

  listPlatform(query?: { status?: string; page?: number; limit?: number; q?: string }) {
    const params = new URLSearchParams();
    if (query?.status) params.set('status', query.status);
    if (query?.page) params.set('page', String(query.page));
    if (query?.limit) params.set('limit', String(query.limit));
    if (query?.q) params.set('q', query.q);
    const qs = params.toString();
    return this.api
      .get<ApiEnvelope<{ items: BlogPostDto[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>>(
        `platform/blogs${qs ? `?${qs}` : ''}`,
      )
      .pipe(map((r) => r.data));
  }

  getPlatformById(id: string) {
    return this.api.get<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}`).pipe(map((r) => r.data));
  }

  createPlatform(payload: CreateBlogPostPayload) {
    return this.api.post<ApiEnvelope<BlogPostDto>>('platform/blogs', payload).pipe(map((r) => r.data));
  }

  updatePlatform(id: string, payload: CreateBlogPostPayload, autosave = false) {
    const qs = autosave ? '?autosave=true' : '';
    return this.api
      .patch<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}${qs}`, payload)
      .pipe(map((r) => r.data));
  }

  validatePublish(id: string) {
    return this.api
      .post<ApiEnvelope<PublishValidationResult>>(`platform/blogs/${id}/validate-publish`, {})
      .pipe(map((r) => r.data));
  }

  publish(id: string) {
    return this.api.post<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}/publish`, {}).pipe(map((r) => r.data));
  }

  schedule(id: string, scheduledAt: string) {
    return this.api
      .post<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}/schedule`, { scheduledAt })
      .pipe(map((r) => r.data));
  }

  unpublish(id: string) {
    return this.api
      .post<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}/unpublish`, {})
      .pipe(map((r) => r.data));
  }

  duplicate(id: string) {
    return this.api
      .post<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}/duplicate`, {})
      .pipe(map((r) => r.data));
  }

  previewToken(id: string) {
    return this.api
      .post<ApiEnvelope<{ token: string; expiresAt: string }>>(`platform/blogs/${id}/preview-token`, {})
      .pipe(map((r) => r.data));
  }

  removePlatform(id: string) {
    return this.api.delete<ApiEnvelope<{ deleted: boolean }>>(`platform/blogs/${id}`).pipe(map((r) => r.data));
  }

  listVersions(id: string) {
    return this.api
      .get<ApiEnvelope<Array<{ _id: string; versionNumber: number; label?: string; createdAt: string }>>>(
        `platform/blogs/${id}/versions`,
      )
      .pipe(map((r) => r.data));
  }

  restoreVersion(id: string, versionId: string) {
    return this.api
      .post<ApiEnvelope<BlogPostDto>>(`platform/blogs/${id}/versions/${versionId}/restore`, {})
      .pipe(map((r) => r.data));
  }

  listCategories() {
    return this.api
      .get<ApiEnvelope<BlogCategoryDto[]>>('platform/blog-categories')
      .pipe(map((r) => r.data));
  }

  createCategory(payload: Partial<BlogCategoryDto>) {
    return this.api
      .post<ApiEnvelope<BlogCategoryDto>>('platform/blog-categories', payload)
      .pipe(map((r) => r.data));
  }

  updateCategory(id: string, payload: Partial<BlogCategoryDto>) {
    return this.api
      .patch<ApiEnvelope<BlogCategoryDto>>(`platform/blog-categories/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  removeCategory(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`platform/blog-categories/${id}`)
      .pipe(map((r) => r.data));
  }

  listAuthors() {
    return this.api.get<ApiEnvelope<BlogAuthorDto[]>>('platform/blog-authors').pipe(map((r) => r.data));
  }

  createAuthor(payload: Partial<BlogAuthorDto>) {
    return this.api.post<ApiEnvelope<BlogAuthorDto>>('platform/blog-authors', payload).pipe(map((r) => r.data));
  }

  updateAuthor(id: string, payload: Partial<BlogAuthorDto>) {
    return this.api
      .patch<ApiEnvelope<BlogAuthorDto>>(`platform/blog-authors/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  removeAuthor(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`platform/blog-authors/${id}`)
      .pipe(map((r) => r.data));
  }

  listSubscribers(page = 1, limit = 50) {
    return this.api
      .get<ApiEnvelope<{ items: Array<{ email: string; fullName?: string; status: string; source: string; sourcePage?: string }> }>>(
        `platform/blog-subscribers?page=${page}&limit=${limit}`,
      )
      .pipe(map((r) => r.data));
  }
}
